import React, { useState, useMemo } from 'react';
import { blogPosts } from '../data/blogs';
import BlogCard from '../components/BlogCard';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import InFeedAd from '../components/Ads/InFeedAd';
import { generateBlogTitleSvgDataUrl, getBlogImageUrl } from '../utils/generateBlogImage';

const BlogList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12; // Increased from 9 for better UX
  const location = useLocation();

  const sortedPosts = useMemo(() => 
    [...blogPosts]
      .map(post => ({
        ...post,
        image: (!post.image || post.image.trim() === '')
          ? generateBlogTitleSvgDataUrl(post.title, (post as any).category)
          : post.image
      }))
      .sort((a, b) => Number(b.id) - Number(a.id)),
    []
  );

  const totalPages = Math.ceil(sortedPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = sortedPosts.slice(indexOfFirstPost, indexOfLastPost);

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageClick = (pageNum: number) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // SEO keywords - OPTIMIZED with topic clustering
  const seoKeywords = useMemo(() => [
    "AI tools blog",
    "AI tutorials 2025",
    "artificial intelligence news",
    "AI automation tips",
    "machine learning guides",
    "AI for business",
    "AI tools reviews",
    "best AI software",
    "generative AI",
    "ChatGPT alternatives",
    ...sortedPosts.slice(0, 8).map(post => post.title)
  ].join(", "), [sortedPosts]);

  // Blog List Schema - ENHANCED
  const blogListSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "TooliQ Blog - AI Tools News & Reviews",
    "description": "Expert insights on AI tools, tutorials, and industry trends. Discover the latest in artificial intelligence, machine learning, and automation.",
    "url": "https://tooliq.ai/blog",
    "publisher": {
      "@type": "Organization",
      "name": "TooliQ",
      "logo": {
        "@type": "ImageObject",
        "url": "https://tooliq.ai/logo.png",
        "width": 300,
        "height": 60
      }
    },
    "blogPost": sortedPosts.slice(0, 10).map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://tooliq.ai/blog/${post.slug}`,
      "datePublished": post.publishedAt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "description": post.excerpt,
      "image": getBlogImageUrl(post.image, post.title, (post as any).category),
      "mainEntityOfPage": `https://tooliq.ai/blog/${post.slug}`
    }))
  }), [sortedPosts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50/50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/10 py-8">
      <Helmet>
        <title>{currentPage === 1 
          ? `TooliQ Blog – ${sortedPosts.length}+ AI Tools Articles, Tutorials & Reviews` 
          : `TooliQ Blog – AI Tools News & Reviews | Page ${currentPage} of ${totalPages}`}
        </title>
        <meta 
          name="description" 
          content={currentPage === 1
            ? `Explore ${sortedPosts.length}+ expert articles on AI tools, machine learning tutorials, automation guides, and artificial intelligence trends. Updated daily with actionable insights.`
            : `Browse page ${currentPage} of ${totalPages}: AI tools reviews, tutorials, and expert insights. Discover the latest trends in artificial intelligence and automation.`
          } 
        />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={`https://tooliq.ai${location.pathname}`} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        
        {/* Pagination meta tags */}
        {currentPage > 1 && (
          <link rel="prev" href={`https://tooliq.ai/blog?page=${currentPage - 1}`} />
        )}
        {currentPage < totalPages && (
          <link rel="next" href={`https://tooliq.ai/blog?page=${currentPage + 1}`} />
        )}
        
        {/* Open Graph */}
        <meta property="og:title" content={`TooliQ Blog – AI Tools News & Reviews${currentPage > 1 ? ` | Page ${currentPage}` : ''}`} />
        <meta property="og:description" content={`${currentPage === 1 ? 'Your go-to resource for' : 'Page ' + currentPage + ' of'} AI tools reviews, tutorials, and expert insights. ${sortedPosts.length}+ articles available.`} />
        <meta property="og:url" content={`https://tooliq.ai${location.pathname}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://tooliq.ai/og-images/blog.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`TooliQ Blog – AI Tools News & Reviews${currentPage > 1 ? ` | Page ${currentPage}` : ''}`} />
        <meta name="twitter:description" content={`Discover ${sortedPosts.length}+ AI tools articles with expert analysis and actionable insights.`} />
        <meta name="twitter:image" content="https://tooliq.ai/og-images/blog.webp" />

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(blogListSchema)
          }}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section - ENHANCED SEO */}
        <div className="text-center py-12">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 text-purple-700 dark:text-purple-400 rounded-full text-sm font-medium mb-6">
            📚 {sortedPosts.length}+ Expert Articles • Updated Daily
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            TooliQ <span className="text-transparent bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text">AI Blog</span>
          </h1>
          
          <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              Discover <strong>{sortedPosts.length}+ in-depth articles</strong> about AI tools, machine learning, 
              generative AI, and automation. Get expert reviews, step-by-step tutorials, and actionable insights 
              to leverage artificial intelligence effectively in 2025 and beyond.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{sortedPosts.length}+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Articles</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalPages}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Pages</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">Daily</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Updates</div>
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Latest Articles
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
          </div>
          
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {currentPosts.map((post, index) => (
              <React.Fragment key={post.id}>
                <BlogCard post={post} />
                
                {/* SINGLE STRATEGIC AD - After 8th post, only on first page */}
                {currentPage === 1 && index === 7 && currentPosts.length > 8 && (
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 my-4">
                    <InFeedAd />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* SEO-Rich Content Section - ENHANCED, only on first page */}
        {currentPage === 1 && (
          <div className="mb-12 bg-white dark:bg-gray-800/50 rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            <div className="prose dark:prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Your Complete Guide to AI Tools & Technology
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                The <strong>TooliQ Blog</strong> is your trusted source for comprehensive AI insights. We publish 
                expert-reviewed articles on <strong>artificial intelligence tools</strong>, <strong>machine learning tutorials</strong>, 
                <strong>automation strategies</strong>, and <strong>generative AI applications</strong>. Our content helps 
                developers, business leaders, and AI enthusiasts make informed decisions about AI adoption.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-5 border border-purple-200 dark:border-purple-700/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                    </svg>
                    Expert Reviews
                  </h3>
                  <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-2">
                    <li>• Hands-on AI tool testing</li>
                    <li>• Unbiased comparisons</li>
                    <li>• Real-world use cases</li>
                    <li>• Pricing analysis</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-5 border border-blue-200 dark:border-blue-700/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Tutorials & Guides
                  </h3>
                  <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-2">
                    <li>• Step-by-step instructions</li>
                    <li>• Code examples</li>
                    <li>• Best practices</li>
                    <li>• Troubleshooting tips</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-5 border border-green-200 dark:border-green-700/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    Industry Insights
                  </h3>
                  <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-2">
                    <li>• AI trends & predictions</li>
                    <li>• Market analysis</li>
                    <li>• Research summaries</li>
                    <li>• Expert interviews</li>
                  </ul>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Whether you're exploring <strong>ChatGPT alternatives</strong>, learning about <strong>AI image generators</strong>, 
                or implementing <strong>business automation</strong>, our blog provides the insights you need to succeed.
              </p>
            </div>
          </div>
        )}

        {/* Pagination - IMPROVED UX */}
        <div className="flex flex-col items-center gap-6 mt-12 mb-8">
          <div className="text-center text-gray-600 dark:text-gray-400 text-sm">
            Showing articles {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, sortedPosts.length)} of {sortedPosts.length}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-800/40 hover:shadow-md'
              }`}
              aria-label="Previous page"
            >
              ← Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }
                
                const isCurrent = pageNum === currentPage;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageClick(pageNum)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                      isCurrent
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    aria-label={`Go to page ${pageNum}`}
                    aria-current={isCurrent ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-800/40 hover:shadow-md'
              }`}
              aria-label="Next page"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Bottom SEO Content - Only on first page */}
        {currentPage === 1 && (
          <div className="mt-12 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="prose dark:prose-invert max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Why Trust TooliQ's AI Blog?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                Our team of <strong>AI experts and industry professionals</strong> tests and reviews hundreds of AI tools 
                monthly. Every article is fact-checked, regularly updated, and written to provide <strong>actionable, 
                unbiased insights</strong> you can trust.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Whether you're a <strong>developer building AI applications</strong>, a <strong>business owner exploring 
                automation</strong>, or an <strong>AI enthusiast staying current</strong> with the latest trends, 
                our blog delivers the knowledge you need to succeed with artificial intelligence.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;